import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

const ProductCard = ({ product, badge }) => {
  const { addItem } = useCart();
  const { customer } = useAuth();
  const discount = product.compare_at_price && product.compare_at_price > product.price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  const toggleWishlist = async (e) => {
    e.preventDefault();
    if (!customer) { toast("Please sign in to save favourites"); return; }
    try {
      await api.post(`/wishlist/${product.id}`);
      toast.success("Saved to wishlist");
    } catch { toast.error("Could not save"); }
  };

  const quickAdd = (e) => {
    e.preventDefault();
    addItem(product, 1, (product.sizes || [])[0] || null);
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/product/${product.slug}`}
        className="group relative block"
        data-testid={`product-card-${product.slug}`}
      >
      <div className="relative overflow-hidden rounded-[20px] bg-blush/40 aspect-[4/5]">
        {product.images?.[0] ? (
          <>
            <img
              src={product.images[0]} alt={product.name}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out ${
                product.images?.[1] ? "group-hover:opacity-0 group-hover:scale-[1.04]" : "group-hover:scale-[1.04]"
              }`}
              loading="lazy"
              data-testid={`product-image-primary-${product.slug}`}
            />
            {product.images?.[1] && (
              <img
                src={product.images[1]} alt={`${product.name} alternate view`}
                className="absolute inset-0 w-full h-full object-cover opacity-0 scale-[1.04] group-hover:opacity-100 group-hover:scale-100 transition-all duration-700 ease-out"
                loading="lazy"
                data-testid={`product-image-hover-${product.slug}`}
              />
            )}
          </>
        ) : (
          <div className="w-full h-full bg-blush" />
        )}
        {/* top-left badge */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {badge && (
            <span className="bg-charcoal text-cream font-label text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full" data-testid="product-badge">{badge}</span>
          )}
          {discount > 0 && (
            <span className="bg-gold text-charcoal font-label text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full">{discount}% off</span>
          )}
        </div>
        {/* wishlist */}
        <button
          onClick={toggleWishlist}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-charcoal hover:text-rose transition"
          aria-label="wishlist"
          data-testid={`wishlist-toggle-${product.slug}`}
        >
          <Heart size={16} />
        </button>
        {/* quick add */}
        <button
          onClick={quickAdd}
          className="absolute left-3 right-3 bottom-3 bg-white/95 backdrop-blur rounded-full py-2.5 font-label text-[11px] uppercase tracking-[0.22em] text-charcoal opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all flex items-center justify-center gap-1.5 hover:bg-charcoal hover:text-cream"
          data-testid={`quick-add-${product.slug}`}
        >
          <Plus size={14} /> Quick add
        </button>
      </div>
      <div className="pt-3 px-1">
        <p className="eyebrow mb-1">{product.category_slug}</p>
        <h3 className="font-cormorant text-lg text-charcoal group-hover:text-rose transition-colors leading-tight" data-testid={`product-name-${product.slug}`}>
          {product.name}
        </h3>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="font-body text-[15px] text-charcoal" data-testid={`product-price-${product.slug}`}>{formatINR(product.price)}</span>
          {product.compare_at_price && product.compare_at_price > product.price && (
            <span className="font-body text-xs text-mauve line-through">{formatINR(product.compare_at_price)}</span>
          )}
        </div>
      </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
